import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import DateTextPicker from '../components/DateTextPicker';
import './Customers.css';
import './BillingDashboard.css';

// Distinct-ish palette for stacked customer bars (first slice can read as teal-adjacent).
const PALETTE = ['#0f8b8d', '#3182ce', '#38a169', '#d69e2e', '#dd6b20', '#805ad5', '#319795', '#e53e3e', '#2b6cb0', '#9f7aea', '#48bb78', '#ed8936', '#4299e1', '#ed64a6', '#a0aec0'];

/** Catmull–Rom → cubic Bézier, open curve through points. */
function smoothCurveThroughPoints(pts) {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i === 0 ? i : i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function fmtMoney(n) {
  if (!n && n !== 0) return '';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export default function BillingDashboard() {
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());
  const today = new Date();
  const defFrom = `${today.getFullYear()}-01-01`;
  const defTo = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [fromDate, setFromDate] = useState(defFrom);
  const [toDate, setToDate] = useState(defTo);
  const [data, setData] = useState({ months: [], series: [], activeSows: [], customers: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);
  const sowLinePathRef = useRef(null);
  const [sowLineLen, setSowLineLen] = useState(0);

  useEffect(() => {
    api.get('/settings/customer-groups').then(setGroups).catch(() => {});
    api.get('/customers').then(setCustomers).catch(() => {});
  }, []);

  const groupFilterOptions = useMemo(
    () => [{ value: '', label: 'All groups' }, ...groups.map(g => ({ value: String(g.id), label: g.group_name || String(g.id) }))],
    [groups]
  );

  const visibleCustomers = useMemo(() => {
    if (!groupFilter) return customers;
    return customers.filter(c => String(c.customer_group_id || '') === String(groupFilter));
  }, [customers, groupFilter]);

  useEffect(() => {
    setSelectedCustomerIds(prev => {
      const allowed = new Set(visibleCustomers.map(c => c.customer_id));
      const next = new Set([...prev].filter(id => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleCustomers]);

  const toggleCustomer = cid => {
    setSelectedCustomerIds(prev => {
      const s = new Set(prev);
      if (s.has(cid)) s.delete(cid); else s.add(cid);
      return s;
    });
  };
  const selectAllVisible = () => setSelectedCustomerIds(new Set(visibleCustomers.map(c => c.customer_id)));
  const clearAllFilters = () => {
    setFilterMenuOpen(null);
    setGroupFilter('');
    setSelectedCustomerIds(new Set());
    setFromDate(defFrom);
    setToDate(defTo);
    setData({ months: [], series: [], activeSows: [], customers: [] });
    setErr('');
  };

  const runReport = async () => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams();
      if (groupFilter) params.set('customer_group_id', groupFilter);
      if (selectedCustomerIds.size > 0) params.set('customer_ids', [...selectedCustomerIds].join(','));
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await api.get(`/dashboards/billing/data?${params}`);
      setData(res);
    } catch (e) {
      setErr(e.message || 'Could not load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const W = 920;
  const H = 460;
  const padL = 60;
  const padR = 76;
  /* Top band for axis titles; plot starts at padT so labels are not crowded by the graph */
  const padT = 40;
  const padB = 50;
  const axisTitleY = 18;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const months = data.months;
  const stackTotals = months.map((_, i) => data.series.reduce((sum, s) => sum + (s.values[i] || 0), 0));
  const yMax = Math.max(1, ...stackTotals);
  const sowMax = Math.max(1, ...data.activeSows);

  const yTicks = 5;
  const yStep = yMax / yTicks;
  const sowStep = sowMax / yTicks;

  const barGroupW = months.length > 0 ? innerW / months.length : 0;
  const barW = Math.max(8, Math.min(48, barGroupW * 0.55));

  const xForMonth = i => padL + barGroupW * (i + 0.5);
  const yForVal = v => padT + innerH - (v / yMax) * innerH;
  const yForSow = v => padT + innerH - (v / sowMax) * innerH;

  const sowPoints = useMemo(() => {
    const bgW = months.length > 0 ? innerW / months.length : 0;
    const xM = i => padL + bgW * (i + 0.5);
    const yS = v => padT + innerH - (v / sowMax) * innerH;
    return data.activeSows.map((v, i) => ({ x: xM(i), y: yS(v) }));
  }, [data.activeSows, months.length, innerW, padL, padT, innerH, sowMax]);

  const sowCurvePath = useMemo(() => smoothCurveThroughPoints(sowPoints), [sowPoints]);

  const sowAreaPath = useMemo(() => {
    if (!sowCurvePath || sowPoints.length === 0) return '';
    const baseY = padT + innerH;
    const last = sowPoints[sowPoints.length - 1];
    const first = sowPoints[0];
    return `${sowCurvePath} L ${last.x} ${baseY} L ${first.x} ${baseY} Z`;
  }, [sowCurvePath, sowPoints, padT, innerH]);

  useLayoutEffect(() => {
    const el = sowLinePathRef.current;
    if (!el || !sowCurvePath) {
      setSowLineLen(0);
      return;
    }
    try {
      setSowLineLen(el.getTotalLength());
    } catch {
      setSowLineLen(0);
    }
  }, [sowCurvePath, months.length]);

  return (
    <>
      <div className="page-header">
        <h1>Billing Dashboard</h1>
      </div>
      <div className="page-content bd-dash-page">
        <div className="customers-top bd-dash-stats-top">
          <div className="customers-stats" aria-label="Dashboard summary">
            <div className="customers-stat">
              <div className="customers-stat-label">Date range</div>
              <div className="customers-stat-value tabular-nums">
                {loading ? '—' : `${fromDate} → ${toDate}`}
              </div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Clients in view</div>
              <div className="customers-stat-value tabular-nums">{visibleCustomers.length.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Selected</div>
              <div className="customers-stat-value tabular-nums">
                {selectedCustomerIds.size > 0 ? selectedCustomerIds.size.toLocaleString() : 'All in view'}
              </div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Months loaded</div>
              <div className="customers-stat-value tabular-nums">{months.length ? months.length.toLocaleString() : '—'}</div>
            </div>
          </div>
        </div>

        <div className="card bd-dash-filters-card">
          <details className="bd-dash-filters-details" open>
            <summary className="bd-dash-filters-summary">
              <span className="bd-dash-filters-chevron" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span>Filters</span>
              <span className="bd-dash-filters-summary-hint tabular-nums">
                {fromDate} → {toDate}
              </span>
              {selectedCustomerIds.size > 0 ? (
                <span className="bd-dash-filters-scope-badge">{selectedCustomerIds.size}</span>
              ) : null}
            </summary>
            <div>
              <div className="form-grid bd-dash-filter-grid">
                <div className="form-group bd-dash-toolbar-filters">
                  <span className="bd-dash-field-label">Customer group</span>
                  <ToolbarFilterDropdown
                    id="bd-dash-group"
                    labelText="Customer group"
                    ariaLabel="Filter by customer group"
                    value={groupFilter}
                    onChange={setGroupFilter}
                    placeholder="All groups"
                    options={groupFilterOptions}
                    menuKey="bd-group"
                    openMenu={filterMenuOpen}
                    setOpenMenu={setFilterMenuOpen}
                    wide
                  />
                </div>
                <DateTextPicker id="bd-dash-from" label="From date" value={fromDate} onChange={setFromDate} />
                <DateTextPicker id="bd-dash-to" label="To date" value={toDate} onChange={setToDate} />
                <div className="form-group bd-dash-filter-actions">
                  <button type="button" className="btn btn-primary" onClick={runReport} disabled={loading}>
                    {loading ? 'Loading…' : 'Run report'}
                  </button>
                </div>
              </div>

              <div className="bd-dash-clients-section">
                <div className="bd-dash-clients-head">
                  <div className="bd-dash-clients-title">
                    Client name
                    {' '}
                    <strong>
                      {selectedCustomerIds.size > 0
                        ? `(${selectedCustomerIds.size} selected)`
                        : '(all in view)'}
                    </strong>
                  </div>
                  <div className="bd-dash-clients-actions">
                    <button type="button" className="btn btn-outline btn-sm" onClick={selectAllVisible}>
                      Select all visible
                    </button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={clearAllFilters}>
                      Clear
                    </button>
                  </div>
                </div>
                <div className="bd-dash-client-grid" role="group" aria-label="Clients to include">
                  {visibleCustomers.map(c => (
                    <label key={c.customer_id} className="bd-dash-client-label">
                      <input
                        type="checkbox"
                        checked={selectedCustomerIds.has(c.customer_id)}
                        onChange={() => toggleCustomer(c.customer_id)}
                      />
                      <span className="bd-dash-client-text" title={c.legal_name || c.trade_name || ''}>
                        {c.legal_name || c.trade_name || c.customer_id}
                      </span>
                    </label>
                  ))}
                  {visibleCustomers.length === 0 ? (
                    <span className="bd-dash-client-empty">No customers in this group.</span>
                  ) : null}
                </div>
              </div>
            </div>
          </details>
        </div>

        {err ? (
          <div className="bd-dash-error" role="alert">
            {err}
          </div>
        ) : null}

        <div className="card bd-dash-chart-card">
          <div className="card-header bd-dash-chart-head">
            <span className="bd-dash-chart-meta">
              Billed amount by month
              <span className="bd-dash-chart-meta-muted">
                {' · '}
                Active SOW trend
                {months.length > 0 ? ` · ${months.length} month${months.length === 1 ? '' : 's'}` : ''}
              </span>
            </span>
          </div>
          <p className="bd-dash-chart-hint">
            <strong>Stacked bars</strong> show billed amount per client, month by month.
            The <strong>curved line</strong> tracks active SOWs on the right-hand scale. Run the report after you change dates or clients.
          </p>
          {months.length === 0 ? (
            <div className="bd-dash-chart-frame">
              <p className="bd-dash-chart-empty">No chart data yet. Set filters and click Run report.</p>
            </div>
          ) : (
            <>
              <div className="bd-dash-chart-frame">
                <svg
                  className="bd-dash-chart-svg"
                  width={W}
                  height={H}
                  viewBox={`0 0 ${W} ${H}`}
                  preserveAspectRatio="xMidYMid meet"
                  role="img"
                  aria-label="Billed amount by month with active SOW trend"
                >
                  <defs>
                    <linearGradient id="bd-dash-sow-line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="var(--warning)" stopOpacity="0.85" />
                      <stop offset="100%" stopColor="#c05621" stopOpacity="1" />
                    </linearGradient>
                    <linearGradient id="bd-dash-sow-area-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="var(--warning)" stopOpacity="0.22" />
                      <stop offset="100%" stopColor="var(--warning)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {Array.from({ length: yTicks + 1 }, (_, i) => {
                    const v = yStep * i;
                    const y = yForVal(v);
                    return (
                      <g key={i} className="bd-dash-grid-row">
                        <line x1={padL} x2={W - padR} y1={y} y2={y} className="bd-dash-grid-line" />
                        <text x={padL - 10} y={y + 4} textAnchor="end" className="bd-dash-axis-tick bd-dash-axis-tick--left">
                          {fmtMoney(v)}
                        </text>
                      </g>
                    );
                  })}
                  {Array.from({ length: yTicks + 1 }, (_, i) => {
                    const v = sowStep * i;
                    const y = yForSow(v);
                    return (
                      <text key={i} x={W - padR + 10} y={y + 4} textAnchor="start" className="bd-dash-axis-tick bd-dash-axis-tick--right">
                        {Math.round(v)}
                      </text>
                    );
                  })}
                  {months.map((m, i) => {
                    let yCursor = yForVal(0);
                    return (
                      <g key={m} className="bd-dash-month-group">
                        {data.series.map((s, sIdx) => {
                          const v = s.values[i] || 0;
                          if (v <= 0) return null;
                          const h = (v / yMax) * innerH;
                          yCursor -= h;
                          const yBottom = yCursor + h;
                          const delayMs = i * 42 + sIdx * 18;
                          return (
                            <g key={s.customer_id} transform={`translate(${xForMonth(i) - barW / 2}, ${yBottom})`}>
                              <g
                                className="bd-dash-bar-grow"
                                style={{ animationDelay: `${delayMs}ms` }}
                              >
                                <rect
                                  x={0}
                                  y={-h}
                                  width={barW}
                                  height={h}
                                  rx={Math.min(3, barW / 4)}
                                  ry={Math.min(3, barW / 4)}
                                  fill={PALETTE[sIdx % PALETTE.length]}
                                  className="bd-dash-bar-rect"
                                >
                                  <title>{`${s.name} · ${m}: ${fmtMoney(v)}`}</title>
                                </rect>
                              </g>
                            </g>
                          );
                        })}
                        <text
                          x={xForMonth(i)}
                          y={yForVal(stackTotals[i]) - 6}
                          textAnchor="middle"
                          className="bd-dash-bar-total"
                        >
                          {stackTotals[i] > 0 ? fmtMoney(stackTotals[i]) : ''}
                        </text>
                        <text x={xForMonth(i)} y={H - padB + 18} textAnchor="middle" className="bd-dash-month-label">
                          {m}
                        </text>
                      </g>
                    );
                  })}
                  {sowAreaPath ? (
                    <path d={sowAreaPath} fill="url(#bd-dash-sow-area-grad)" className="bd-dash-sow-area" />
                  ) : null}
                  {sowCurvePath ? (
                    <path
                      key={sowCurvePath}
                      ref={sowLinePathRef}
                      d={sowCurvePath}
                      fill="none"
                      stroke="url(#bd-dash-sow-line-grad)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="bd-dash-sow-line"
                      strokeDasharray={sowLineLen > 0 ? sowLineLen : undefined}
                      strokeDashoffset={sowLineLen > 0 ? sowLineLen : undefined}
                    />
                  ) : null}
                  {data.activeSows.map((v, i) => (
                    <g key={i}>
                      <circle
                        cx={xForMonth(i)}
                        cy={yForSow(v)}
                        r="5"
                        fill="var(--card-bg)"
                        stroke="var(--warning)"
                        strokeWidth="2.25"
                        className="bd-dash-sow-dot"
                        style={{ animationDelay: `${280 + i * 55}ms` }}
                      >
                        <title>{`Active SOWs in ${months[i]}: ${v}`}</title>
                      </circle>
                      <text
                        x={xForMonth(i)}
                        y={yForSow(v) - 12}
                        textAnchor="middle"
                        className="bd-dash-sow-value"
                        style={{ animationDelay: `${320 + i * 55}ms` }}
                      >
                        {v}
                      </text>
                    </g>
                  ))}
                  <line x1={padL} x2={padL} y1={padT} y2={H - padB} className="bd-dash-axis-line" />
                  <line x1={padL} x2={W - padR} y1={H - padB} y2={H - padB} className="bd-dash-axis-line" />
                  <line x1={W - padR} x2={W - padR} y1={padT} y2={H - padB} className="bd-dash-axis-line bd-dash-axis-line--right" />
                  <text x={padL} y={axisTitleY} textAnchor="start" className="bd-dash-axis-title bd-dash-axis-title--left">
                    Billed amount
                  </text>
                  <text
                    x={W - 12}
                    y={axisTitleY}
                    textAnchor="end"
                    className="bd-dash-axis-title bd-dash-axis-title--right"
                  >
                    Active SOWs
                  </text>
                </svg>
              </div>
              <div className="bd-dash-legend">
                {data.series.map((s, i) => (
                  <span key={s.customer_id} className="bd-dash-legend-item">
                    <span className="bd-dash-legend-swatch" style={{ background: PALETTE[i % PALETTE.length] }} aria-hidden />
                    {s.name}
                  </span>
                ))}
                <span className="bd-dash-legend-item bd-dash-legend-sow">
                  <span className="bd-dash-legend-line bd-dash-legend-line--curve" aria-hidden />
                  Active SOWs (right scale · curve)
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
